import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Form, Input, DatePicker, List, message, Space, Typography, Tag, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CommentOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router';
import { treatmentApi } from '../api/treatment';
import { CustomEnumType, CustomEnumValue, CustomField, CustomParamScenario, ExtraValue, TreatmentRecord } from '../api/model';
import { CreateTreatmentRecordRequest, UpdateTreatmentRecordRequest, AddTreatmentRecordCommentRequest } from '../api/treatment';
import { customEnumApi } from '../api/custom_enum';
import { customFieldApi } from '../api/custom_field';
import { useCurrentUser } from '../util/hooks';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// 定义固定的条件逻辑常量
const ORDER_BEFORE_FIXED = (order: number) => order < 0;
const ORDER_AFTER_FIXED = (order: number) => order >= 0;

interface LocationState {
  patientName?: string;
}

const TreatmentRecords: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const location = useLocation();
  const state = location.state as LocationState;
  const [patientName, setPatientName] = useState(state?.patientName || '');
  const navigate = useNavigate();
  const [treatmentRecords, setTreatmentRecords] = useState<TreatmentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<TreatmentRecord | null>(null);
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [commentForms, setCommentForms] = useState<Record<string, string>>({});
  const currentUser = useCurrentUser();
  const [modal, contextHolder] = Modal.useModal();
  
  // CustomEnum和CustomField相关状态
  const [customEnumTypes, setCustomEnumTypes] = useState<CustomEnumType[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [customEnumValuesMap, setCustomEnumValuesMap] = useState<Record<string, CustomEnumValue[]>>({});
  const [selectedExtraValues, setSelectedExtraValues] = useState<ExtraValue[]>([]);
  
  // 检查当前医生是否为记录创建者
  const isRecordOwner = (record: TreatmentRecord) => {
    // 添加安全检查，确保所有必要的对象都存在
    if (!record || !record.doctor || !record.doctor.user || !currentUser || !currentUser.id) {
      return false;
    }
    return record.doctor.user.id === currentUser.id;
  };

  // 获取治疗记录列表
  const fetchTreatmentRecords = async () => {
    setLoading(true);
    try {
      const response = await treatmentApi.getTreatmentRecordsByPatientID(patientId!);
      if (response.success) {
        setTreatmentRecords(response.data.data);
      } else {
        message.error(response.message || '获取治疗记录失败');
      }
    } catch (error) {
      console.error('获取治疗记录失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 提交创建表单
  const handleCreateSubmit = async (values: any) => {
    if (!patientId) return;
    
    try {
      const requestData: CreateTreatmentRecordRequest = {
        patient_id: patientId,
        treatment_date: values.treatment_date.format('YYYY-MM-DD'),
        content: values.content,
        extra: selectedExtraValues
      };

      const response = await treatmentApi.createTreatmentRecord(requestData);
      if (response.success) {
        message.success('治疗记录创建成功');
        setCreateModalVisible(false);
        form.resetFields();
        // 清空选中的额外值
        setSelectedExtraValues([]);
        // 重新获取治疗记录列表
        fetchTreatmentRecords();
      } else {
        message.error(response.message || '创建治疗记录失败');
      }
    } catch (error) {
      console.error('创建治疗记录失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 提交更新表单
  const handleUpdateSubmit = async (values: any) => {
    if (!currentRecord || !patientId) return;
    
    try {
      const requestData: UpdateTreatmentRecordRequest = {
        treatment_date: values.treatment_date.format('YYYY-MM-DD'),
        content: values.content,
        extra: selectedExtraValues
      };

      const response = await treatmentApi.updateTreatmentRecord(currentRecord.id, requestData);
      if (response.success) {
        message.success('治疗记录更新成功');
        setUpdateModalVisible(false);
        updateForm.resetFields();
        setCurrentRecord(null);
        // 清空选中的额外值
        setSelectedExtraValues([]);
        // 重新获取治疗记录列表
        fetchTreatmentRecords();
      } else {
        message.error(response.message || '更新治疗记录失败');
      }
    } catch (error) {
      console.error('更新治疗记录失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 删除治疗记录
  const handleDelete = async (recordId: string) => {
    modal.confirm({
      title: '确认删除',
      content: '确定要删除这条治疗记录吗？此操作不可恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await treatmentApi.deleteTreatmentRecord(recordId);
          if (response.success) {
            message.success('治疗记录删除成功');
            // 重新获取治疗记录列表
            fetchTreatmentRecords();
          } else {
            message.error(response.message || '删除治疗记录失败');
          }
        } catch (error) {
          console.error('删除治疗记录失败:', error);
          message.error('网络错误，请稍后重试');
        }
      },
    });
  };

  // 添加评论
  const handleAddComment = async (recordId: string) => {
    const content = commentForms[recordId];
    if (!content) {
      message.warning('请输入评论内容');
      return;
    }
    
    try {
      const requestData: AddTreatmentRecordCommentRequest = {
        content,
      };

      const response = await treatmentApi.addTreatmentRecordComment(recordId, requestData);
      if (response.success) {
        message.success('评论添加成功');
        // 清空评论输入框
        setCommentForms(prev => ({ ...prev, [recordId]: '' }));
        // 重新获取治疗记录列表
        fetchTreatmentRecords();
      } else {
        message.error(response.message || '添加评论失败');
      }
    } catch (error) {
      console.error('添加评论失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 获取CustomEnum类型和值，以及CustomField
  const fetchCustomEnumsAndFields = async () => {
    try {
      // 获取treatment场景的枚举类型
      const enumTypesResponse = await customEnumApi.listCustomEnumTypes(CustomParamScenario.Treatment);
      if (enumTypesResponse.success) {
        let enumTypes = enumTypesResponse.data;
        // 按order字段排序枚举类型
        enumTypes = enumTypes.sort((a, b) => a.order - b.order);
        setCustomEnumTypes(enumTypes);
        
        // 获取每个枚举类型对应的枚举值
        const enumValuesMap: Record<string, CustomEnumValue[]> = {};
        for (const type of enumTypes) {
          const enumValuesResponse = await customEnumApi.listCustomEnumValues(type.id);
          if (enumValuesResponse.success) {
            let enumValues = enumValuesResponse.data;
            // 按order字段排序枚举值
            enumValues = enumValues.sort((a, b) => a.order - b.order);
            enumValuesMap[type.id] = enumValues;
          }
        }
        setCustomEnumValuesMap(enumValuesMap);
      }

      // 获取treatment场景的自定义字段
      const customFieldsResponse = await customFieldApi.listCustomFields(CustomParamScenario.Treatment);
      if (customFieldsResponse.success) {
        let fields = customFieldsResponse.data;
        // 按order字段排序自定义字段
        fields = fields.sort((a, b) => a.order - b.order);
        setCustomFields(fields);
      }
    } catch (error) {
      console.error('获取CustomEnum和CustomField失败:', error);
      message.error('获取自定义参数失败');
    }
  };

  // 初始化获取数据
  useEffect(() => {
    if (!patientName && patientId) {
      // 这里可以调用API获取患者详细信息
      // 暂时使用患者ID作为名称
      setPatientName(`患者${patientId}`);
    }
    
    if (patientId) {
      fetchTreatmentRecords();
      fetchCustomEnumsAndFields();
    }
  }, [patientId, patientName]);

  // 处理CheckableTag选择
  const handleTagCheck = useCallback((typeId: string, valueId: string, checked: boolean, mode: string) => {
    setSelectedExtraValues(prev => {
      // 查找当前类型的ExtraValue
      const existingIndex = prev.findIndex(item => item.type === 'enum' && item.enum_type_id === typeId);
      
      if (mode === 'single') {
        // 单选模式：替换为新值
        const newExtraValues = prev.filter(item => !(item.type === 'enum' && item.enum_type_id === typeId));
        if (checked) {
          newExtraValues.push({
            type: 'enum',
            enum_type_id: typeId,
            selected_enum_value_id: valueId
          });
        }
        return newExtraValues;
      } else {
        // 多选模式：添加或移除值
        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          let selectedIds = existing.selected_enum_value_ids || [];
          if (checked) {
            // 添加值
            if (!selectedIds.includes(valueId)) {
              selectedIds = [...selectedIds, valueId];
            }
          } else {
            // 移除值
            selectedIds = selectedIds.filter(id => id !== valueId);
          }
          
          if (selectedIds.length > 0) {
            // 更新现有项
            return prev.map((item, index) => {
              if (index === existingIndex) {
                return {
                  ...item,
                  selected_enum_value_ids: selectedIds
                };
              }
              return item;
            });
          } else {
            // 删除该项
            return prev.filter((_, index) => index !== existingIndex);
          }
        } else {
          // 创建新项
          if (checked) {
            return [...prev, {
              type: 'enum',
              enum_type_id: typeId,
              selected_enum_value_ids: [valueId]
            }];
          }
          return prev;
        }
      }
    });
  }, []);

  // 打开更新模态框
  const openUpdateModal = (record: TreatmentRecord) => {
    setCurrentRecord(record);
    setUpdateModalVisible(true);
    // 设置表单初始值
    updateForm.setFieldsValue({
      treatment_date: dayjs(record.treatment_date),
      content: record.content,
    });
    setSelectedExtraValues(record.extra || []);
  };

  // 处理评论输入变化
  const handleCommentChange = useCallback((recordId: string, value: string) => {
    setCommentForms(prev => ({ ...prev, [recordId]: value }));
  }, []);

  // 获取患者姓名
  useEffect(() => {
    if (!patientName && patientId) {
      // 这里可以调用API获取患者详细信息
      // 暂时使用患者ID作为名称
      setPatientName(`患者${patientId}`);
    }
  }, [patientName, patientId]);

  // 处理字段值变化
  const handleFieldValueChange = useCallback((fieldId: string, value: string) => {
    setSelectedExtraValues(prev => {
      // 查找当前字段的ExtraValue
      const existingIndex = prev.findIndex(item => item.type === 'field' && item.field_id === fieldId);
      
      if (existingIndex >= 0) {
        // 更新现有项
        return prev.map((item, index) => {
          if (index === existingIndex) {
            return {
              ...item,
              field_value: value
            };
          }
          return item;
        });
      } else {
        // 创建新项
        return [...prev, {
          type: 'field',
          field_id: fieldId,
          field_value: value
        }];
      }
    });
  }, []);

  // 处理额外值的辅助函数
  const renderExtraValues = (record: TreatmentRecord, condition: (order: number) => boolean) => {
    // 获取所有自定义字段和枚举类型的映射
    const enumTypeMap = new Map(customEnumTypes.map(type => [type.id, type]));
    const fieldMap = new Map(customFields.map(field => [field.id, field]));
    
    // 处理自定义参数，添加类型信息
    const processedExtra = (record.extra || []).map(extraItem => {
      const itemType = extraItem.type || 'enum'; // 兼容旧数据
      
      if (itemType === 'enum') {
        const enumType = enumTypeMap.get(extraItem.enum_type_id!);
        return {
          extraItem,
          type: 'enum',
          name: enumType?.name || '未知枚举类型',
          order: enumType?.order || 0,
          enumType
        };
      } else {
        const field = fieldMap.get(extraItem.field_id!);
        return {
          extraItem,
          type: 'field',
          name: field?.name || '未知自定义字段',
          order: field?.order || 0,
          field
        };
      }
    }).filter(item => item.order !== undefined);
    
    // 按order排序
    processedExtra.sort((a, b) => a.order - b.order);
    
    // 过滤符合条件的自定义参数
    const filteredFields = processedExtra.filter(item => condition(item.order));
    if (filteredFields.length === 0) return null;
    
    return filteredFields.map((item) => {
      const { extraItem, type, name } = item;
      
      if (type === 'enum') {
        // 渲染枚举类型值
        const enumValues = customEnumValuesMap[extraItem.enum_type_id!] || [];
        let selectedValues: CustomEnumValue[] = [];
        
        if (extraItem.selected_enum_value_id) {
          const selectedValue = enumValues.find(value => value.id === extraItem.selected_enum_value_id);
          if (selectedValue) {
            selectedValues = [selectedValue];
          }
        } else if (extraItem.selected_enum_value_ids) {
          selectedValues = enumValues.filter(value => 
            extraItem.selected_enum_value_ids?.includes(value.id)
          );
        }
        
        if (selectedValues.length === 0) return null;
        
        return (
          <Descriptions.Item key={extraItem.enum_type_id!} label={name}>
            <Space>
              {selectedValues.map(value => (
                <Tag key={value.id} color="blue">
                  {value.name}
                </Tag>
              ))}
            </Space>
          </Descriptions.Item>
        );
      } else {
        // 渲染自定义字段值
        const fieldValue = extraItem.field_value || '';
        
        return (
          <Descriptions.Item key={extraItem.field_id!} label={name}>
            {fieldValue}
          </Descriptions.Item>
        );
      }
    });
  };

  return (
    <div>
      {contextHolder}
      <Card 
        title={`治疗记录 - ${patientName}`} 
        extra={
            <Space>
              <Button onClick={() => navigate('/patients')}>
                返回患者列表
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => setCreateModalVisible(true)}
              >
                添加治疗记录
              </Button>
            </Space>
          }
      >
        <List
          loading={loading}
          dataSource={treatmentRecords}
          renderItem={record => (
            <Card 
              key={record.id} 
              style={{ marginBottom: 16 }}
            >
              <div style={{ position: 'relative', paddingRight: isRecordOwner(record) ? 80 : 0 }}>
                <Title level={4}>{dayjs(record.treatment_date).format('YYYY-MM-DD')}</Title>
                <Descriptions column={2}>
                  {/* 分离并展示order<0的自定义参数，显示在固定参数前面 */}
                  {renderExtraValues(record, ORDER_BEFORE_FIXED)}
                  
                  {/* 固定参数 */}
                  <Descriptions.Item label="医生">
                    {record.doctor?.user?.name || '未知医生'}
                  </Descriptions.Item>
                  <Descriptions.Item label="创建时间">
                    {new Date(record.create_time).toLocaleString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="内容" span={2}>
                    {record.content}
                  </Descriptions.Item>
                  
                  {/* 处理额外值，显示order>=0的自定义参数，显示在固定参数后面 */}
                  {renderExtraValues(record, ORDER_AFTER_FIXED)}
                </Descriptions>
                {isRecordOwner(record) && (
                  <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center' }}>
                    <Button 
                      type="link" 
                      icon={<EditOutlined />} 
                      onClick={() => openUpdateModal(record)}
                      style={{ padding: '4px 8px' }}
                    >
                      编辑
                    </Button>
                    <Button 
                      type="link" 
                      icon={<DeleteOutlined />} 
                      danger
                      onClick={() => handleDelete(record.id)}
                      style={{ padding: '4px 8px' }}
                    >
                      删除
                    </Button>
                  </div>
                )}
              </div>
              
              <div style={{ marginTop: 16 }}>
                <Title level={5}>评论</Title>
                {record.comments && record.comments.length > 0 ? (
                  <List
                    dataSource={record.comments}
                    renderItem={comment => (
                      <Card size="small" style={{ marginBottom: 8 }}>
                        <Text strong>{comment.creator_name || '未知用户'}:</Text> {comment.content}
                        <br />
                        <Text type="secondary">{new Date(comment.create_time).toLocaleString()}</Text>
                      </Card>
                    )}
                  />
                ) : (
                  <Text type="secondary">暂无评论</Text>
                )}
                
                <div style={{ marginTop: 16 }}>
                  <Input.TextArea 
                    placeholder="添加评论..." 
                    value={commentForms[record.id] || ''}
                    onChange={e => handleCommentChange(record.id, e.target.value)}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CommentOutlined />} 
                    onClick={() => handleAddComment(record.id)}
                    style={{ marginTop: 8 }}
                    disabled={!isRecordOwner(record)}
                  >
                    提交评论
                  </Button>
                </div>
              </div>
            </Card>
          )}
        />
      </Card>

      {/* 创建治疗记录模态框 */}
      <Modal
        title="新建治疗记录"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
          // 清空选中的额外值
          setSelectedExtraValues([]);
        }}
        onOk={() => form.submit()}
        okText="创建"
        cancelText="取消"
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {/* order<0的字段展示在固定字段之前 */}
          <RenderExtraFieldsFormItems 
            condition={ORDER_BEFORE_FIXED}
            customEnumTypes={customEnumTypes}
            customFields={customFields}
            customEnumValuesMap={customEnumValuesMap}
            selectedExtraValues={selectedExtraValues}
            handleTagCheck={handleTagCheck}
            handleFieldValueChange={handleFieldValueChange}
          />
          
          <Form.Item
            name="treatment_date"
            label="治疗日期"
            rules={[{ required: true, message: '请选择治疗日期' }]}
          >
            <DatePicker placeholder="请选择治疗日期" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="content"
            label="治疗记录内容"
            rules={[{ required: true, message: '请输入治疗记录内容' }]}
          >
            <Input.TextArea placeholder="请输入治疗记录内容" autoSize={{ minRows: 3, maxRows: 10 }} />
          </Form.Item>
          
          {/* order>=0的字段展示在固定字段之后 */}
          <RenderExtraFieldsFormItems 
            condition={ORDER_AFTER_FIXED}
            customEnumTypes={customEnumTypes}
            customFields={customFields}
            customEnumValuesMap={customEnumValuesMap}
            selectedExtraValues={selectedExtraValues}
            handleTagCheck={handleTagCheck}
            handleFieldValueChange={handleFieldValueChange}
          />
        </Form>
      </Modal>

      {/* 更新治疗记录模态框 */}
      <Modal
        title="编辑治疗记录"
        open={updateModalVisible}
        onCancel={() => {
          setUpdateModalVisible(false);
          updateForm.resetFields();
          setCurrentRecord(null);
          // 清空选中的额外值
          setSelectedExtraValues([]);
        }}
        onOk={() => updateForm.submit()}
        okText="更新"
        cancelText="取消"
        width={800}
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdateSubmit}>
          {/* order<0的字段展示在固定字段之前 */}
          <RenderExtraFieldsFormItems 
            condition={ORDER_BEFORE_FIXED}
            customEnumTypes={customEnumTypes}
            customFields={customFields}
            customEnumValuesMap={customEnumValuesMap}
            selectedExtraValues={selectedExtraValues}
            handleTagCheck={handleTagCheck}
            handleFieldValueChange={handleFieldValueChange}
          />
          
          <Form.Item
            name="treatment_date"
            label="治疗日期"
            rules={[{ required: true, message: '请选择治疗日期' }]}
          >
            <DatePicker placeholder="请选择治疗日期" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="content"
            label="治疗记录内容"
            rules={[{ required: true, message: '请输入治疗记录内容' }]}
          >
            <Input.TextArea placeholder="请输入治疗记录内容" autoSize={{ minRows: 3, maxRows: 10 }} />
          </Form.Item>
          
          {/* order>=0的字段展示在固定字段之后 */}
          <RenderExtraFieldsFormItems 
            condition={ORDER_AFTER_FIXED}
            customEnumTypes={customEnumTypes}
            customFields={customFields}
            customEnumValuesMap={customEnumValuesMap}
            selectedExtraValues={selectedExtraValues}
            handleTagCheck={handleTagCheck}
            handleFieldValueChange={handleFieldValueChange}
          />
        </Form>
      </Modal>
    </div>
  );
};

// 定义RenderExtraFieldsFormItems组件的props类型
interface RenderExtraFieldsFormItemsProps {
  condition: (order: number) => boolean;
  customEnumTypes: CustomEnumType[];
  customFields: CustomField[];
  customEnumValuesMap: Record<string, CustomEnumValue[]>;
  selectedExtraValues: ExtraValue[];
  handleTagCheck: (typeId: string, valueId: string, checked: boolean, mode: string) => void;
  handleFieldValueChange: (fieldId: string, value: string) => void;
}

// 合并CustomEnumType和CustomField，创建统一的字段列表
const getCombinedFields = (customEnumTypes: CustomEnumType[], customFields: CustomField[]) => {
  // 转换为统一格式
  const enumFields = customEnumTypes.map(type => ({
    id: type.id,
    name: type.name,
    order: type.order,
    type: 'enum' as const,
    mode: type.mode,
    enumType: type
  }));
  
  const fieldFields = customFields.map(field => ({
    id: field.id,
    name: field.name,
    order: field.order,
    type: 'field' as const,
    field: field
  }));
  
  // 合并并排序
  const combined = [...enumFields, ...fieldFields];
  
  // 按order排序：order<0的在前，按order升序；order>=0的在后，按order升序
  combined.sort((a, b) => {
    return a.order - b.order;
  });
  
  return combined;
};

// 渲染自定义字段的辅助组件
const RenderExtraFieldsFormItems: React.FC<RenderExtraFieldsFormItemsProps> = ({
  condition,
  customEnumTypes,
  customFields,
  customEnumValuesMap,
  selectedExtraValues,
  handleTagCheck,
  handleFieldValueChange
}) => {
  const combinedFields = getCombinedFields(customEnumTypes, customFields);
  
  return (
    <>
      {combinedFields
        .filter(field => condition(field.order))
        .map(field => {
          if (field.type === 'enum') {
            // 渲染枚举类型字段
            const enumType = field.enumType;
            return (
              <Form.Item key={`${condition(field.order) ? 'before' : 'after'}-${field.id}`} label={field.name}>
                <Space wrap>
                  {customEnumValuesMap[field.id]?.map(value => {
                    // 检查是否已选中
                    const isChecked = selectedExtraValues.some(item => 
                      item.type === 'enum' && 
                      item.enum_type_id === field.id && 
                      (item.selected_enum_value_id === value.id || 
                       item.selected_enum_value_ids?.includes(value.id))
                    );
                    return (
                      <Tag.CheckableTag
                        key={value.id}
                        checked={isChecked}
                        onChange={(checked) => handleTagCheck(field.id, value.id, checked, enumType.mode)}
                      >
                        {value.name}
                      </Tag.CheckableTag>
                    );
                  })}
                </Space>
              </Form.Item>
            );
          } else if (field.type === 'field') {
            // 渲染自定义字段
            const customField = field.field;
            // 查找当前字段的值
            const existingValue = selectedExtraValues.find(item => 
              item.type === 'field' && item.field_id === field.id
            )?.field_value || '';
            
            return (
              <Form.Item key={`${condition(field.order) ? 'before' : 'after'}-${field.id}`} label={field.name}>
                {customField.is_multiline ? (
                  <Input.TextArea
                    placeholder={customField.hint || '请输入'}
                    value={existingValue}
                    onChange={e => handleFieldValueChange(field.id, e.target.value)}
                    autoSize={{ minRows: 3, maxRows: 10 }}
                  />
                ) : (
                  <Input
                    placeholder={customField.hint || '请输入'}
                    value={existingValue}
                    onChange={e => handleFieldValueChange(field.id, e.target.value)}
                  />
                )}
              </Form.Item>
            );
          }
          // 对于未知类型，返回null，不渲染
          return null;
        })}
    </>
  );
};

export default TreatmentRecords;